import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { hapticsImpactLight } from '../../../../utils/haptics';
import { useLocation, useNavigate } from 'react-router-dom';
import { paths } from '@/routes/paths';
import { TabTypes } from '../../../../types/Global';
import { RootState } from '../../../../store/store';
import { useSelector } from 'react-redux';
import { Chart } from '../../../../assets/svgs/Chart';
import { ChartActive } from '../../../../assets/svgs/ChartActive';
import { VideosActive } from '../../../../assets/svgs/VideosActive';
import { Videos } from '../../../../assets/svgs/Videos';
import { More, MoreActive } from '../../../../assets/svgs/More';
import { TreeView, TreeViewActive } from '../../../../assets/svgs/TreeView';
import { Joystick, ListChecks, SignOut } from '@phosphor-icons/react';
import { signOut } from '../../../../services/auth';
import {
  buildAuthPath,
  getCurrentPathWithSearch,
} from '../../../../utils/authRedirect';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Home } from '@/assets/svgs/Home';
import { HomeActive } from '@/assets/svgs/HomeActive';
import { examsSelectors } from '../../../../slices/exams/examsSlice';
import { isVideosOffForExam } from '../../../../utils/examUtils';
import {
  useCapability,
  useIsCourseStudent,
} from '../../../../instances/useInstance';

interface AppBarProps {}

const AppBar: React.FC<AppBarProps> = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  // NCLEX-RN has no videos at launch: no Videos tab (the route redirects home).
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  // The desktop NavBar's gates: a partner-course student (Courses V1) has
  // Modules in place of Videos, and an instance without Ora's study library
  // has no free-study tab. Partner commerce drops Games from the NavBar's
  // onboarded rail; this bar has no Games tab there to drop.
  const isCourseStudent = useIsCourseStudent();
  const hasStudyLibrary = useCapability('studyLibrary') !== 'none';

  const showsVideosTab =
    !isVideosOffForExam(primaryExam) && hasStudyLibrary && !isCourseStudent;

  const [activeTab, setActiveTab] = useState<TabTypes>(TabTypes.Home);
  const innerRef = useRef<HTMLDivElement>(null);

  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
  } | null>(null);

  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isGamesRoute = pathname.startsWith('/games');
  const isGuestGamesRoute = !isAuthenticated && isGamesRoute;

  const isPreOnboardingGamesRoute =
    isAuthenticated && currentUser?.isOnboarded === false && isGamesRoute;

  const shouldRenderGamesOnlyNav = Boolean(isPreOnboardingGamesRoute);

  useEffect(() => {
    if (pathname === '/dashboard') {
      setActiveTab(TabTypes.Home);
    } else if (pathname.includes('/games')) {
      setActiveTab(TabTypes.Games);
    } else if (pathname === '/performance') {
      setActiveTab(TabTypes.Stats);
    } else if (pathname === '/modules') {
      setActiveTab(TabTypes.Modules);
    } else if (pathname.includes('/videos')) {
      setActiveTab(TabTypes.Videos);
    } else if (pathname.includes('/topics')) {
      setActiveTab(TabTypes.Topics);
    } else if (
      pathname === '/account' ||
      pathname.startsWith('/help') ||
      pathname === '/qbank-start'
    ) {
      setActiveTab(TabTypes.Account);
    } else {
      setActiveTab(TabTypes.null);
    }
  }, [pathname]);

  // Follow the active tab's box. Re-measured whenever the row's size changes
  // (rotation, a tab set swapping in, the fluid tab padding easing) so the
  // highlight cannot drift away from the tab it marks.
  useLayoutEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;

    const measure = () => {
      const active = inner.querySelector<HTMLElement>(
        '.app-bar_inner_button.active'
      );

      setIndicator(
        active ? { left: active.offsetLeft, width: active.offsetWidth } : null
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [
    activeTab,
    currentUser?.isOnboarded,
    isCourseStudent,
    isGuestGamesRoute,
    showsVideosTab,
  ]);

  return (
    <>
      <nav className="app-bar">
        <div className="app-bar_inner" ref={innerRef}>
          {/* The active tab's highlight: one element that slides to the new
              tab rather than five that pop in and out. Hidden until measured
              (and on the routes with no tab of their own). */}
          <span
            aria-hidden="true"
            className="app-bar_inner_indicator"
            hidden={!indicator}
            style={
              indicator
                ? {
                    transform: `translateX(${indicator.left}px)`,
                    width: `${indicator.width}px`,
                  }
                : undefined
            }
          />
          <button
            className={`app-bar_inner_button button ${
              activeTab === (isGuestGamesRoute ? TabTypes.Games : TabTypes.Home)
                ? 'active'
                : ''
            }`}
            onClick={() => {
              hapticsImpactLight();
              navigate(isGuestGamesRoute ? '/games' : '/dashboard');
            }}
            aria-label={
              isGuestGamesRoute ? 'Navigate to Games' : 'Navigate to Home'
            }
            tabIndex={0}
          >
            <div className="app-bar_inner_button_icon">
              {isGuestGamesRoute ? (
                <Joystick
                  weight={activeTab === TabTypes.Games ? 'fill' : 'regular'}
                />
              ) : activeTab === TabTypes.Home ? (
                <HomeActive />
              ) : (
                <Home />
              )}
            </div>
            {/* The route is `/dashboard`, but on the tab bar it is home. */}
            <span>{isGuestGamesRoute ? 'Games' : 'Home'}</span>
          </button>
          {shouldRenderGamesOnlyNav ? (
            <>
              <button
                className={`app-bar_inner_button button ${activeTab === TabTypes.Games ? 'active' : ''}`}
                onClick={() => {
                  hapticsImpactLight();
                  navigate(paths.games);
                }}
                aria-label="Navigate to Games"
                tabIndex={0}
              >
                <div className="app-bar_inner_button_icon">
                  <Joystick
                    weight={activeTab === TabTypes.Games ? 'fill' : 'regular'}
                  />
                </div>
                <span>Games</span>
              </button>
              <DropdownMenu
                open={accountMenuOpen}
                onOpenChange={setAccountMenuOpen}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    className={`app-bar_inner_button button button--account ${activeTab === TabTypes.Account ? 'active' : ''}`}
                    onClick={() => {
                      hapticsImpactLight();
                    }}
                    aria-label="Access account settings"
                    tabIndex={0}
                  >
                    <div className="app-bar_inner_button_icon">
                      {activeTab === TabTypes.Account ? (
                        <MoreActive />
                      ) : (
                        <More />
                      )}
                    </div>
                    <span>Account</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => {
                      setAccountMenuOpen(false);
                      hapticsImpactLight();
                      void signOut().then(() => navigate(paths.auth));
                    }}
                  >
                    <SignOut style={{ marginRight: '.5rem' }} />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : currentUser?.isOnboarded ? (
            <>
              <button
                id="topics-tab"
                className={`app-bar_inner_button button ${activeTab === TabTypes.Topics ? 'active' : ''}`}
                onClick={() => {
                  hapticsImpactLight();
                  navigate(paths.topics);
                }}
                aria-label="Navigate to Topics"
                tabIndex={0}
              >
                <div className="app-bar_inner_button_icon">
                  {activeTab === TabTypes.Topics ? (
                    <TreeViewActive />
                  ) : (
                    <TreeView />
                  )}
                </div>
                <span>Topics</span>
              </button>
              <button
                className={`app-bar_inner_button button ${activeTab === TabTypes.Stats ? 'active' : ''}`}
                onClick={() => {
                  hapticsImpactLight();
                  navigate(paths.performance);
                }}
                aria-label="Navigate to Performance"
                tabIndex={0}
              >
                <div className="app-bar_inner_button_icon">
                  {activeTab === TabTypes.Stats ? <ChartActive /> : <Chart />}
                </div>
                <span>Stats</span>
              </button>
              {isCourseStudent && (
                <button
                  className={`app-bar_inner_button button ${activeTab === TabTypes.Modules ? 'active' : ''}`}
                  onClick={() => {
                    hapticsImpactLight();
                    navigate(paths.modules);
                  }}
                  aria-label="Navigate to Modules"
                  tabIndex={0}
                >
                  <div className="app-bar_inner_button_icon">
                    <ListChecks
                      weight={
                        activeTab === TabTypes.Modules ? 'fill' : 'regular'
                      }
                      aria-hidden="true"
                    />
                  </div>
                  <span>Modules</span>
                </button>
              )}
              {showsVideosTab && (
                <button
                  id="videos-tab"
                  className={`app-bar_inner_button button ${activeTab === TabTypes.Videos ? 'active' : ''}`}
                  onClick={() => {
                    hapticsImpactLight();
                    navigate(paths.videos);
                  }}
                  aria-label="Navigate to Videos"
                  tabIndex={0}
                >
                  <div className="app-bar_inner_button_icon">
                    {activeTab === TabTypes.Videos ? (
                      <VideosActive />
                    ) : (
                      <Videos />
                    )}
                  </div>
                  <span>Videos</span>
                </button>
              )}
              <button
                className={`app-bar_inner_button button button--account ${activeTab === TabTypes.Account ? 'active' : ''}`}
                onClick={() => {
                  hapticsImpactLight();
                  if (isAuthenticated) {
                    navigate(paths.accountMenu);
                    return;
                  }

                  navigate(buildAuthPath(getCurrentPathWithSearch()));
                }}
                aria-label={
                  isAuthenticated ? 'Access account settings' : 'Sign in'
                }
                tabIndex={0}
              >
                <div className="app-bar_inner_button_icon">
                  {activeTab === TabTypes.Account ? <MoreActive /> : <More />}
                </div>
                <span>More</span>
              </button>
            </>
          ) : null}
        </div>
      </nav>
    </>
  );
};

export default AppBar;
